'use client';

import { c as _c } from "react/compiler-runtime";
import type { Fetchable } from '@morpho-org/blue-sdk';
import type { ColumnDefWithSkeleton } from '@morpho-org/ui-v6/types';
import { cn } from '@morpho-org/ui-v6/utils';
import { Icon, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { Arrow } from '@repo/ui/icons';
import { createSortedRowModel, flexRender, type HeaderGroup, type OnChangeFn, type PaginationState, type Row, type RowData, rowPaginationFeature, rowSortingFeature, type SortDirection, sortFns, type SortingState, tableFeatures, type TableState, useTable } from '@tanstack/react-table';
import { column_getCanSort, column_getIsSorted, column_toggleSorting, row_getVisibleCells } from '@tanstack/react-table/static-functions';
import { ScrollArea } from 'radix-ui';
import { type CSSProperties, type PropsWithChildren, type ReactNode, useCallback, useMemo, useState } from 'react';
import { Pagination } from '../Pagination';

// No paginated row model: callers that paginate do it server-side. The pagination feature stays
// registered for page state and the getCan*Page controls.
const searchMenuTableFeatures = tableFeatures({
  rowPaginationFeature,
  rowSortingFeature,
  sortFns,
  sortedRowModel: createSortedRowModel()
});
type SearchMenuTableFeatures = typeof searchMenuTableFeatures;
export type { SearchMenuTableFeatures };

/**
 * Table row styles - Tailwind replica of dapp-ui-v5 StyledSearchMenuTableRow.
 * Each row is its own `display: table` with fixed layout so cell widths line up
 * with the header row.
 */
const tableRowStyles = 'table w-full table-fixed';
const dataRowStyles = cn([tableRowStyles, 'h-10', 'focus-visible:outline-border focus-visible:outline-2 focus-visible:-outline-offset-2', 'hover:bg-background hover:cursor-pointer', '[&>td:first-of-type]:pl-sm [&>td:last-of-type]:pr-sm']);

/**
 * Header cell styles - Tailwind replica of dapp-ui-v5 StyledSearchMenuTableHeader.
 */
const tableHeaderCellStyles = cn(['relative h-[38px] text-start whitespace-nowrap', 'text-3xs text-secondary-foreground', 'border-border border-b-[0.5px]']);
export interface TableHeaderCellProps extends PropsWithChildren {
  columnName?: string;
  colSpan: number;
  children: ReactNode;
  isSorted?: false | SortDirection;
  isSortable?: boolean;
  isPinned?: boolean;
  onClick: () => void;
  width?: CSSProperties['width'];
}
const TableHeaderCell = t0 => {
  const $ = _c(18);
  const {
    columnName,
    colSpan,
    children,
    isSorted: t1,
    isSortable,
    onClick,
    width
  } = t0;
  const isSorted = t1 === undefined ? false : t1;
  const [isHovered, setIsHovered] = useState(false);
  const tooltipContent = columnName ? `Sort by ${columnName}` : "Sort";
  const isArrowVisible = isSortable && (isHovered || Boolean(isSorted));
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("td-content", tableHeaderCellStyles);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== width) {
    t3 = {
      width
    };
    $[1] = width;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setIsHovered(true);
    t5 = () => setIsHovered(false);
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== isArrowVisible || $[6] !== isSortable || $[7] !== isSorted || $[8] !== onClick || $[9] !== tooltipContent) {
    t6 = isSortable && <Tooltip><TooltipTrigger asChild={true}><div className={cn("w-5 flex cursor-pointer items-center justify-center", "hover:text-foreground hover:opacity-100", isSorted ? "text-secondary-foreground" : "text-muted-foreground/40", isArrowVisible ? "opacity-100" : "opacity-0")} onClick={onClick}><Icon icon={<Arrow />} className={cn("size-5", isSorted === "asc" ? "[transform:rotate(-90deg)]" : "[transform:rotate(90deg)_translateY(-0.5px)]")} /></div></TooltipTrigger><TooltipContent>{tooltipContent}</TooltipContent></Tooltip>;
    $[5] = isArrowVisible;
    $[6] = isSortable;
    $[7] = isSorted;
    $[8] = onClick;
    $[9] = tooltipContent;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== children || $[12] !== t6) {
    t7 = <div className="inline-flex items-center">{children}{t6}</div>;
    $[11] = children;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== colSpan || $[15] !== t3 || $[16] !== t7) {
    t8 = <th colSpan={colSpan} className={t2} style={t3} onMouseEnter={t4} onMouseLeave={t5}>{t7}</th>;
    $[14] = colSpan;
    $[15] = t3;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
};
export interface TableHeaderProps<T extends RowData> {
  headerGroup: HeaderGroup<SearchMenuTableFeatures, T>;
  columnLabels?: Record<string, string>;
  columnWidths?: CSSProperties['width'][];
}
const TableHeader = <T extends RowData,>({
  headerGroup,
  columnLabels,
  columnWidths
}: TableHeaderProps<T>) => {
  // 'use no memo': TanStack keeps this object's reference while table state (sorting, rows) changes.
  'use no memo';

  return <tr className={tableRowStyles}>
      {headerGroup.headers.map((header, index) => <TableHeaderCell key={header.id} columnName={columnLabels?.[header.id]} colSpan={header.colSpan} isSorted={column_getIsSorted(header.column)} isSortable={column_getCanSort(header.column)} width={columnWidths?.[index]} onClick={() => {
      if (column_getCanSort(header.column)) column_toggleSorting(header.column);
    }}>
          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
        </TableHeaderCell>)}
    </tr>;
};
export interface TableRowProps<T extends RowData> {
  row?: Row<SearchMenuTableFeatures, T>;
  skeletonRow?: Fetchable<ReactNode[]>;
  onRowClick?: (rowData: T) => void;
  columnWidths?: CSSProperties['width'][];
}
const TableRow = <T extends RowData,>({
  row,
  skeletonRow,
  onRowClick,
  columnWidths
}: TableRowProps<T>) => {
  // 'use no memo': TanStack keeps this object's reference while table state (sorting, rows) changes.
  'use no memo';

  const handleRowClick = useCallback(() => {
    if (row?.original) {
      onRowClick?.(row.original);
    }
  }, [onRowClick, row]);
  return <tr role="row" tabIndex={0} className={dataRowStyles} onClick={handleRowClick} onKeyDown={e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleRowClick();
    }
  }}>
      {skeletonRow && skeletonRow.map((cell, index) => <td className="td-content" style={{
      width: columnWidths?.[index]
    }} key={index} role="cell">
            {cell}
          </td>)}
      {row && row_getVisibleCells(row).map((cell_0, index_0) => <td className="td-content" style={{
      width: columnWidths?.[index_0]
    }} key={cell_0.id} role="cell">
            {flexRender(cell_0.column.columnDef.cell, cell_0.getContext())}
          </td>)}
    </tr>;
};
export interface SearchMenuTableProps<T extends RowData> {
  columnWidths?: CSSProperties['width'][];
  loading?: boolean;
  data: T[];
  columnsDefs: ColumnDefWithSkeleton<SearchMenuTableFeatures, T>[];
  onRowClick?: (rowData: T) => void;
  emptyMessage?: ReactNode;
  onSortingChange?: OnChangeFn<SortingState>;
  state?: Partial<TableState<SearchMenuTableFeatures>>;
  manualSorting?: boolean;
  manualPagination?: boolean;
  pageCount?: number;
  onPaginationChange?: OnChangeFn<PaginationState>;
  totalItemsCount?: number | null;
  queryError?: Error;
  skeletonRowsCount?: number;
  maxHeight?: CSSProperties['maxHeight'];
  height?: CSSProperties['height'];
}

/**
 * SearchMenuTable component - Tailwind replica of dapp-ui-v5 SearchMenuTable.
 */
const SearchMenuTable = t0 => {
  const $ = _c(66);
  const {
    loading,
    data,
    columnsDefs,
    columnWidths,
    onRowClick,
    onSortingChange,
    emptyMessage,
    state,
    manualSorting: t1,
    manualPagination: t2,
    pageCount,
    onPaginationChange,
    totalItemsCount,
    queryError,
    skeletonRowsCount: t3,
    maxHeight,
    height
  } = t0;
  const manualSorting = t1 === undefined ? false : t1;
  const manualPagination = t2 === undefined ? false : t2;
  const skeletonRowsCount = t3 === undefined ? 2 : t3;
  let t4;
  if ($[0] !== onSortingChange) {
    t4 = onSortingChange && {
      onSortingChange
    };
    $[0] = onSortingChange;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== onPaginationChange) {
    t5 = onPaginationChange && {
      onPaginationChange
    };
    $[2] = onPaginationChange;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== columnsDefs || $[5] !== data || $[6] !== manualPagination || $[7] !== manualSorting || $[8] !== pageCount || $[9] !== state || $[10] !== t4 || $[11] !== t5) {
    t6 = {
      features: searchMenuTableFeatures,
      data,
      columns: columnsDefs,
      enableSorting: true,
      enableSortingRemoval: false,
      state,
      manualSorting,
      manualPagination,
      pageCount,
      ...t4,
      ...t5
    };
    $[4] = columnsDefs;
    $[5] = data;
    $[6] = manualPagination;
    $[7] = manualSorting;
    $[8] = pageCount;
    $[9] = state;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const table = useTable(t6);
  let t7;
  if ($[13] !== columnsDefs || $[14] !== skeletonRowsCount) {
    let t8;
    if ($[16] !== columnsDefs) {
      t8 = () => columnsDefs.map(_temp);
      $[16] = columnsDefs;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    t7 = Array.from({
      length: skeletonRowsCount
    }, t8);
    $[13] = columnsDefs;
    $[14] = skeletonRowsCount;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const skeletonRows = t7;
  const showEmptyState = !loading && data.length === 0 && emptyMessage;
  if (showEmptyState) {
    let t8;
    if ($[18] !== height || $[19] !== maxHeight) {
      t8 = {
        maxHeight,
        height
      };
      $[18] = height;
      $[19] = maxHeight;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    let t9;
    if ($[21] !== emptyMessage) {
      t9 = <span className="text-3xs">{emptyMessage}</span>;
      $[21] = emptyMessage;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    let t10;
    if ($[23] !== t8 || $[24] !== t9) {
      t10 = <div className="p-lg flex items-center justify-center" style={t8}>{t9}</div>;
      $[23] = t8;
      $[24] = t9;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    return t10;
  }
  let t8;
  if ($[26] !== height || $[27] !== maxHeight) {
    t8 = {
      maxHeight,
      height
    };
    $[26] = height;
    $[27] = maxHeight;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== columnWidths || $[30] !== table) {
    let t10;
    if ($[32] !== columnWidths) {
      t10 = headerGroup => <TableHeader key={headerGroup.id} headerGroup={headerGroup} columnWidths={columnWidths} />;
      $[32] = columnWidths;
      $[33] = t10;
    } else {
      t10 = $[33];
    }
    t9 = table.getHeaderGroups().map(t10);
    $[29] = columnWidths;
    $[30] = table;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[34] !== t9) {
    t10 = <thead className="bg-card sticky top-0 z-1 [background-image:linear-gradient(to_bottom,var(--color-muted),var(--color-muted))]">{t9}</thead>;
    $[34] = t9;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== columnWidths || $[37] !== data || $[38] !== loading || $[39] !== onRowClick || $[40] !== table) {
    t11 = !loading && data.length > 0 && table.getRowModel().rows.map(row => <TableRow key={row.id} row={row} onRowClick={onRowClick} columnWidths={columnWidths} />);
    $[36] = columnWidths;
    $[37] = data;
    $[38] = loading;
    $[39] = onRowClick;
    $[40] = table;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== columnWidths || $[43] !== loading || $[44] !== skeletonRows) {
    t12 = loading && skeletonRows?.map((row_0, index) => <TableRow key={index + "skeleton-table-search-menu"} skeletonRow={row_0} columnWidths={columnWidths} />);
    $[42] = columnWidths;
    $[43] = loading;
    $[44] = skeletonRows;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  let t13;
  if ($[46] !== t11 || $[47] !== t12) {
    t13 = <tbody>{t11}{t12}</tbody>;
    $[46] = t11;
    $[47] = t12;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  let t14;
  if ($[49] !== t10 || $[50] !== t13) {
    t14 = <table className="w-full border-separate border-spacing-0 p-0 tabular-nums">{t10}{t13}</table>;
    $[49] = t10;
    $[50] = t13;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] !== loading || $[53] !== manualPagination || $[54] !== onPaginationChange || $[55] !== queryError || $[56] !== state?.pagination || $[57] !== table || $[58] !== totalItemsCount) {
    t15 = onPaginationChange && manualPagination && <div className="p-md flex w-full justify-center"><Pagination loading={loading ?? false} error={queryError} pagination={state?.pagination} totalItemsCount={totalItemsCount} previousPage={table.previousPage} nextPage={table.nextPage} setPage={table.setPageIndex} canPreviousPage={table.getCanPreviousPage()} canNextPage={table.getCanNextPage()} /></div>;
    $[52] = loading;
    $[53] = manualPagination;
    $[54] = onPaginationChange;
    $[55] = queryError;
    $[56] = state?.pagination;
    $[57] = table;
    $[58] = totalItemsCount;
    $[59] = t15;
  } else {
    t15 = $[59];
  }
  let t16;
  let t17;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <ScrollArea.Scrollbar orientation="vertical"><ScrollArea.Thumb /></ScrollArea.Scrollbar>;
    t17 = <ScrollArea.Corner />;
    $[60] = t16;
    $[61] = t17;
  } else {
    t16 = $[60];
    t17 = $[61];
  }
  let t18;
  if ($[62] !== t14 || $[63] !== t15 || $[64] !== t8) {
    t18 = <ScrollArea.Root><ScrollArea.Root className="grid w-full overflow-auto" style={t8}>{t14}{t15}{t16}{t17}</ScrollArea.Root></ScrollArea.Root>;
    $[62] = t14;
    $[63] = t15;
    $[64] = t8;
    $[65] = t18;
  } else {
    t18 = $[65];
  }
  return t18;
};
export { SearchMenuTable };
function _temp(def) {
  return def.skeletonCell;
}